import {
  genericValidationRules,
  PCP_FAX_DIGIT_PATTERN,
  PCP_FIELD_LIMITS,
} from '@/utilities/formValidations'

export type PcpNameAndContactValues = {
  pcpName: string
  pcpFax: string
  pcpAddressLine1: string
  pcpAddressLine2: string
  pcpCity: string
  pcpState: string
  pcpZipCode: string
}

export type PcpNameAndContactFieldErrors = {
  pcpName?: string
  pcpFax?: string
  pcpAddressLine1?: string
  pcpAddressLine2?: string
  pcpCity?: string
  pcpState?: string
  pcpZipCode?: string
}

/** Completeness/format keys. Hosts map these to copy. Zip/length still use genericValidationRules strings. */
export const PCP_CONTACT_ERROR = {
  pcpNameRequired: 'pcp_name_required',
  faxRequired: 'fax_required',
  faxOrAddressRequired: 'fax_or_address_required',
  faxInvalid: 'fax_invalid',
  streetRequired: 'street_required',
  cityRequired: 'city_required',
  stateRequired: 'state_required',
} as const

export type PcpContactErrorKey = (typeof PCP_CONTACT_ERROR)[keyof typeof PCP_CONTACT_ERROR]

/** Which PCP contact channel(s) to collect. `either` = fax or complete address. */
export type PcpContactMode = 'either' | 'fax' | 'mail'

/**
 * Collapse LD fax/mail booleans into one mode. Both off falls back to fax so patients
 * always have a contact path.
 */
export function resolvePcpContactMode(showPcpFax: boolean, showPcpMail: boolean): PcpContactMode {
  if (showPcpFax && showPcpMail) return 'either'
  if (showPcpMail) return 'mail'
  return 'fax'
}

export function pcpContactModeVisibility(mode: PcpContactMode): {
  showFax: boolean
  showMail: boolean
} {
  return {
    showFax: mode === 'either' || mode === 'fax',
    showMail: mode === 'either' || mode === 'mail',
  }
}

export function pcpFaxDigits(value: string): string {
  return String(value).replace(/\D/g, '')
}

/**
 * 10-digit US national number. Strips a leading country code 1 when the value
 * is 11 digits (e.g. `+1 (617) 555-1212` or `16175551212`).
 */
export function pcpFaxNationalNumber(value: string): string {
  const digits = pcpFaxDigits(value)
  if (digits.length === PCP_FIELD_LIMITS.pcpFaxDigits + 1 && digits.startsWith('1')) {
    return digits.slice(1)
  }
  return digits
}

const PCP_FAX_FORMAT_PATTERN = /^\+?[\d\s()-]+$/

export function isPcpFaxFormatValid(value: string): boolean {
  const trimmed = String(value).trim()
  if (!trimmed) return true
  if (!PCP_FAX_FORMAT_PATTERN.test(trimmed)) return false
  const national = pcpFaxNationalNumber(trimmed)
  return PCP_FAX_DIGIT_PATTERN.test(national) && national.length === PCP_FIELD_LIMITS.pcpFaxDigits
}

export function normalizePcpFaxForPayload(value: string): string | null {
  const trimmed = String(value).trim()
  if (!trimmed) return null
  return pcpFaxNationalNumber(trimmed)
}

export function anyPcpAddressFieldEntered(pcp: PcpNameAndContactValues): boolean {
  return [pcp.pcpAddressLine1, pcp.pcpAddressLine2, pcp.pcpCity, pcp.pcpState, pcp.pcpZipCode].some(
    (v) => String(v).trim() !== '',
  )
}

export function pcpAddressIsComplete(
  pcp: Pick<PcpNameAndContactValues, 'pcpAddressLine1' | 'pcpCity' | 'pcpState' | 'pcpZipCode'>,
): boolean {
  return (
    pcp.pcpAddressLine1.trim() !== '' &&
    pcp.pcpCity.trim() !== '' &&
    pcp.pcpState.trim() !== '' &&
    pcp.pcpZipCode.trim() !== ''
  )
}

function applyIncompletePcpAddressErrors(
  pcp: PcpNameAndContactValues,
  errors: PcpNameAndContactFieldErrors,
): void {
  if (!pcp.pcpAddressLine1.trim()) {
    errors.pcpAddressLine1 = PCP_CONTACT_ERROR.streetRequired
  }
  if (!pcp.pcpCity.trim()) {
    errors.pcpCity = PCP_CONTACT_ERROR.cityRequired
  }
  if (!pcp.pcpState.trim()) {
    errors.pcpState = PCP_CONTACT_ERROR.stateRequired
  }
  const zipErr = genericValidationRules.zipCode(pcp.pcpZipCode)
  if (zipErr) errors.pcpZipCode = zipErr
}

function applyPcpFaxWhenNoAddress(
  pcpFax: string,
  errors: PcpNameAndContactFieldErrors,
  allowAddressAlternative: boolean,
): void {
  const trimmed = String(pcpFax).trim()
  if (trimmed && isPcpFaxFormatValid(pcpFax)) return
  if (trimmed !== '') {
    errors.pcpFax = PCP_CONTACT_ERROR.faxInvalid
    return
  }
  errors.pcpFax = allowAddressAlternative
    ? PCP_CONTACT_ERROR.faxOrAddressRequired
    : PCP_CONTACT_ERROR.faxRequired
}

function applyPcpFaxAndZipFormatErrors(
  pcp: PcpNameAndContactValues,
  errors: PcpNameAndContactFieldErrors,
): void {
  if (String(pcp.pcpFax).trim() !== '' && !isPcpFaxFormatValid(pcp.pcpFax)) {
    errors.pcpFax = PCP_CONTACT_ERROR.faxInvalid
  }
  if (pcp.pcpZipCode.trim() !== '') {
    const zipErr = genericValidationRules.zipCode(pcp.pcpZipCode)
    if (zipErr) errors.pcpZipCode = zipErr
  }
}

function validatePcpContact(
  pcp: PcpNameAndContactValues,
  errors: PcpNameAndContactFieldErrors,
  contactMode: PcpContactMode,
): void {
  if (contactMode === 'fax') {
    // Address UI is hidden; incomplete/complete autofilled address is handled on submit
    // (complete persists, incomplete is dropped) — do not validate invisible fields.
    applyPcpFaxWhenNoAddress(pcp.pcpFax, errors, false)
    return
  }

  if (contactMode === 'mail') {
    // Fax UI is hidden; invalid/valid autofilled fax is handled on submit.
    applyIncompletePcpAddressErrors(pcp, errors)
    return
  }

  const anyAddr = anyPcpAddressFieldEntered(pcp)
  const addrComplete = pcpAddressIsComplete(pcp)

  if (anyAddr && !addrComplete) {
    applyIncompletePcpAddressErrors(pcp, errors)
  } else if (!anyAddr) {
    applyPcpFaxWhenNoAddress(pcp.pcpFax, errors, true)
  }
  applyPcpFaxAndZipFormatErrors(pcp, errors)
}

export function pcpAddressFitsFieldLimits(pcp: PcpNameAndContactValues): boolean {
  const address1Err = genericValidationRules.maxLength(
    'Street name',
    PCP_FIELD_LIMITS.pcpAddressLine1,
  )(pcp.pcpAddressLine1)
  const address2Err = genericValidationRules.maxLength(
    'Address line 2',
    PCP_FIELD_LIMITS.pcpAddressLine2,
  )(pcp.pcpAddressLine2)
  const cityErr = genericValidationRules.maxLength('City', PCP_FIELD_LIMITS.pcpCity)(pcp.pcpCity)
  const stateErr = pcp.pcpState.trim()
    ? genericValidationRules.exactLength('State', PCP_FIELD_LIMITS.pcpState)(pcp.pcpState)
    : undefined
  const zipErr = pcp.pcpZipCode.trim() ? genericValidationRules.zipCode(pcp.pcpZipCode) : undefined
  return !address1Err && !address2Err && !cityErr && !stateErr && !zipErr
}

function emptyPcpFieldToNull(value: string | null | undefined): string | null {
  const trimmed = String(value ?? '').trim()
  return trimmed === '' ? null : trimmed
}

/** Form answers used to build API PCP contact + flags (Patient and Client kit registration). */
export type PcpAnswersForMapping = PcpNameAndContactValues & {
  hasPcp?: boolean | null
  needPcpHelp?: boolean | null
  sendFaxToPcp?: boolean
}

export type PcpAnswersMappingOptions = {
  /** When false, share-results UI is hidden; API always gets send-fax-results true. Default true. */
  shareResultsQuestionVisible?: boolean
  /** When false, need-help UI is hidden; API gets needPcpHelp null. Default true. */
  needPcpHelpQuestionVisible?: boolean
}

export type PcpMappedContact = {
  pcpName: string | null
  pcpFax: string | null
  pcpAddress1: string | null
  pcpAddress2: string | null
  pcpCity: string | null
  pcpState: string | null
  pcpZip: string | null
}

export type PcpMappedAnswers =
  | {
      hasPcp: false
      needPcpHelp: boolean | null
      sendFaxResultsToPcp: null
      contact: null
    }
  | {
      hasPcp: true
      needPcpHelp: false
      sendFaxResultsToPcp: boolean
      contact: PcpMappedContact
    }

function noPcpMappedAnswers(
  needPcpHelp: boolean | null,
): Extract<PcpMappedAnswers, { hasPcp: false }> {
  return {
    hasPcp: false,
    needPcpHelp,
    sendFaxResultsToPcp: null,
    contact: null,
  }
}

function mapHasPcpContact(
  pcp: PcpAnswersForMapping,
  shareVisible: boolean,
): Extract<PcpMappedAnswers, { hasPcp: true }> {
  const digits = pcpFaxNationalNumber(pcp.pcpFax)
  const hasValidFax = digits.length === PCP_FIELD_LIMITS.pcpFaxDigits
  const addrComplete = pcpAddressIsComplete(pcp) && pcpAddressFitsFieldLimits(pcp)

  return {
    hasPcp: true,
    needPcpHelp: false,
    sendFaxResultsToPcp: shareVisible ? Boolean(pcp.sendFaxToPcp) : true,
    contact: {
      pcpName: pcp.pcpName.trim() || null,
      pcpFax: hasValidFax ? digits : null,
      pcpAddress1: addrComplete ? emptyPcpFieldToNull(pcp.pcpAddressLine1) : null,
      pcpAddress2: addrComplete ? emptyPcpFieldToNull(pcp.pcpAddressLine2) : null,
      pcpCity: addrComplete ? emptyPcpFieldToNull(pcp.pcpCity) : null,
      pcpState: addrComplete ? emptyPcpFieldToNull(pcp.pcpState) : null,
      pcpZip: addrComplete ? emptyPcpFieldToNull(pcp.pcpZipCode) : null,
    },
  }
}

/**
 * Map PCP questionnaire answers to contact + flags.
 *
 * Payload builders only rename fields (snake_case `default_pcp` vs camelCase `contact`).
 * Incomplete or over-limit address is dropped; fax is 10 national digits or null.
 * Unanswered `hasPcp` matches Patient Portal: needPcpHelp is false when that question is shown.
 */
export function mapPcpAnswersToContactAndFlags(
  pcp: PcpAnswersForMapping | undefined,
  options: PcpAnswersMappingOptions = {},
): PcpMappedAnswers {
  const shareVisible = options.shareResultsQuestionVisible !== false
  const needHelpVisible = options.needPcpHelpQuestionVisible !== false

  if (pcp?.hasPcp == null) {
    return noPcpMappedAnswers(needHelpVisible ? false : null)
  }
  if (pcp.hasPcp === true) {
    return mapHasPcpContact(pcp, shareVisible)
  }
  return noPcpMappedAnswers(needHelpVisible ? pcp.needPcpHelp === true : null)
}

function applyPcpLengthErrors(
  pcp: PcpNameAndContactValues,
  errors: PcpNameAndContactFieldErrors,
  contactMode: PcpContactMode,
): void {
  const nameErr = genericValidationRules.maxLength(
    'PCP Name',
    PCP_FIELD_LIMITS.pcpName,
  )(pcp.pcpName)
  if (nameErr) errors.pcpName = nameErr

  // Hidden mail fields are dropped or persisted on submit; do not block Next for them.
  if (!pcpContactModeVisibility(contactMode).showMail) {
    return
  }

  const address1Err = genericValidationRules.maxLength(
    'Street name',
    PCP_FIELD_LIMITS.pcpAddressLine1,
  )(pcp.pcpAddressLine1)
  if (address1Err) errors.pcpAddressLine1 = address1Err
  const address2Err = genericValidationRules.maxLength(
    'Address line 2',
    PCP_FIELD_LIMITS.pcpAddressLine2,
  )(pcp.pcpAddressLine2)
  if (address2Err) errors.pcpAddressLine2 = address2Err
  const cityErr = genericValidationRules.maxLength('City', PCP_FIELD_LIMITS.pcpCity)(pcp.pcpCity)
  if (cityErr) errors.pcpCity = cityErr
  if (pcp.pcpState.trim()) {
    const stateErr = genericValidationRules.exactLength(
      'State',
      PCP_FIELD_LIMITS.pcpState,
    )(pcp.pcpState)
    if (stateErr) errors.pcpState = stateErr
  }
}

export type ValidatePcpNameAndContactOptions = {
  /** Default `either` (Client Portal order placement). */
  contactMode?: PcpContactMode
}

export type SyncPcpAddressFieldErrorsOptions = ValidatePcpNameAndContactOptions & {
  /** When false, refresh address errors only (used after leaving the address group). Default true. */
  includeFax?: boolean
}

export const PCP_ADDRESS_FIELD_KEYS = [
  'pcpAddressLine1',
  'pcpAddressLine2',
  'pcpCity',
  'pcpState',
  'pcpZipCode',
] as const

export type PcpAddressFieldKey = (typeof PCP_ADDRESS_FIELD_KEYS)[number]

export function isPcpAddressField(key: string): key is PcpAddressFieldKey {
  return (PCP_ADDRESS_FIELD_KEYS as readonly string[]).includes(key)
}

export const EMPTY_PCP_ADDRESS_FIELDS: Pick<PcpNameAndContactValues, PcpAddressFieldKey> = {
  pcpAddressLine1: '',
  pcpAddressLine2: '',
  pcpCity: '',
  pcpState: '',
  pcpZipCode: '',
}

export const PCP_ADDRESS_INVALID_CLEAR_MESSAGE =
  'The address is invalid. Either clear it or fix the errors above.'

/** Validates PCP name and fax-or-address contact (kit registration when hasPcp, order placement when adding PCP). */
export function validatePcpNameAndContact(
  pcp: PcpNameAndContactValues,
  options: ValidatePcpNameAndContactOptions = {},
): PcpNameAndContactFieldErrors {
  const errors: PcpNameAndContactFieldErrors = {}
  if (!pcp.pcpName.trim()) {
    errors.pcpName = PCP_CONTACT_ERROR.pcpNameRequired
  }
  const contactMode = options.contactMode ?? 'either'
  validatePcpContact(pcp, errors, contactMode)
  applyPcpLengthErrors(pcp, errors, contactMode)
  return errors
}

/** Single-field error for blur, matching address-info under-field validation. */
export function validatePcpNameAndContactField(
  pcp: PcpNameAndContactValues,
  field: keyof PcpNameAndContactFieldErrors,
  options: ValidatePcpNameAndContactOptions = {},
): string | undefined {
  const contactMode = options.contactMode ?? 'either'
  const error = validatePcpNameAndContact(pcp, options)[field]
  if (!error) return undefined
  if (contactMode !== 'either') return error
  if (field !== 'pcpFax' && !isPcpAddressField(field)) return error

  const faxComplete = Boolean(pcp.pcpFax.trim()) && isPcpFaxFormatValid(pcp.pcpFax)
  const addressComplete = pcpAddressIsComplete(pcp) && pcpAddressFitsFieldLimits(pcp)
  if (faxComplete || addressComplete) return error

  // Still filling fax or address: only flag values the user already entered.
  const fieldValue = field === 'pcpFax' ? pcp.pcpFax : pcp[field]
  if (!String(fieldValue).trim()) return undefined
  return error
}

/** Whether the address group currently fails contact validation (incomplete or format). */
export function pcpAddressHasFieldErrors(
  pcp: PcpNameAndContactValues,
  options: ValidatePcpNameAndContactOptions = {},
): boolean {
  const errors = validatePcpNameAndContact(pcp, options)
  return PCP_ADDRESS_FIELD_KEYS.some((key) => Boolean(errors[key]))
}

const PCP_ADDRESS_CONTACT_ERROR_KEYS = [...PCP_ADDRESS_FIELD_KEYS, 'pcpFax'] as const

/**
 * Refresh address and fax errors from the full contact rules. Used when the
 * address is emptied so leftover field errors drop and fax can become required again.
 */
export function syncPcpAddressFieldErrors<T extends PcpNameAndContactFieldErrors>(
  current: T,
  pcp: PcpNameAndContactValues,
  options: SyncPcpAddressFieldErrorsOptions = {},
): T {
  const { includeFax = true, ...validateOptions } = options
  const validated = validatePcpNameAndContact(pcp, validateOptions)
  const next = { ...current }
  const keys = includeFax ? PCP_ADDRESS_CONTACT_ERROR_KEYS : PCP_ADDRESS_FIELD_KEYS
  for (const key of keys) {
    if (validated[key]) {
      next[key] = validated[key]
    } else {
      delete next[key]
    }
  }
  return next
}

/** Whether PCP fields pass validation for enabling Next / Place order */
export function isOrderPlacementPcpMinimallyComplete(
  form: PcpNameAndContactValues & { isAddingPcpInfo?: boolean },
): boolean {
  if (!form.isAddingPcpInfo) return true
  return Object.keys(validatePcpNameAndContact(form)).length === 0
}

function formString(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  return ''
}

export function toPcpNameAndContactFromForm(
  form: Record<string, unknown>,
): PcpNameAndContactValues {
  return {
    pcpName: formString(form.pcpName),
    pcpFax: formString(form.pcpFax),
    pcpAddressLine1: formString(form.pcpAddressLine1),
    pcpAddressLine2: formString(form.pcpAddressLine2),
    pcpCity: formString(form.pcpCity),
    pcpState: formString(form.pcpState),
    pcpZipCode: formString(form.pcpZipCode),
  }
}
